import React, { useState, useEffect } from "react";
import {
  Layout,
  Row,
  Col,
  Card,
  Button,
  Input,
  Dropdown,
  Table,
  Modal,
  message,
  Upload,
} from "antd";
import {
  UnorderedListOutlined,
  ShoppingCartOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import DefaultLayout from "../../components/layout/DefaultLayout";
import payment from "../../services/payment";
import { useSelector } from "react-redux";
import generateInvoice from "../../utils/pdf";

const OrdersPage = () => {
  const { confirm } = Modal;

  const { currentUser } = useSelector((state) => state.user);
  const isAdmin = currentUser?.roles?.includes("Admin");
  const isDriver = currentUser?.roles?.includes("Driver");
  const isAdminOrDriver = isAdmin || isDriver;

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [visible, setVisible] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState([
    "id",
    "customer_name",
    "whatsapp",
    "address",
    "miles",
    "subtotal",
    "created_at",
    "transaction",
    "boxes",
    "upload",
    "invoice",
  ]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [editingOrder, setEditingOrder] = useState(null);

  // Fetch orders
  const fetchOrders = async () => {
    setLoading(true);
    try {
      const data = await payment.getOrders();
      setOrders(data.orders || []);
    } catch (err) {
      console.error(err);
      message.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  // Columns
  const columns = [
    { title: "Order ID", dataIndex: "id", key: "id" },
    {
      title: "Transaction ID",
      key: "transaction",
      render: (_, record) => record.transaction?.square_payment_id || "-",
    },
    { title: "Customer", dataIndex: "customer_name", key: "customer_name" },
    { title: "WhatsApp", dataIndex: "whatsapp", key: "whatsapp" },
    { title: "Address", dataIndex: "address", key: "address" },
    { title: "Miles", dataIndex: "miles", key: "miles" },
    { title: "Subtotal", dataIndex: "subtotal", key: "subtotal" },
    {
      title: "Boxes",
      key: "boxes",
      render: (_, record) => {
        if (!record.boxes || record.boxes.length === 0) return "-";
        return record.boxes
          .map(
            (b, i) =>
              `#${i + 1}: W:${b.weight} L:${b.length} W:${b.width} H:${b.height}`
          )
          .join(" | ");
      },
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (date) => {
        if (!date) return "-";
        const d = new Date(date);
        return `${d.getDate().toString().padStart(2, "0")}-${(
          d.getMonth() + 1
        )
          .toString()
          .padStart(2, "0")}-${d.getFullYear()} ${d
          .getHours()
          .toString()
          .padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
      },
    },
    {
      title: "Upload",
      key: "upload",
      render: (_, record) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            type="primary"
            style={{ background: "#3C3C3B !important" }}
            onClick={() => {
              setEditingOrder(record);

              setFileList(
                (record.photos || record.attachment || []).map((url, index) => ({
                  uid: `-${index}`,
                  name: url.split("/").pop(),
                  status: "done",
                  url: url.startsWith("http")
                    ? url
                    : `http://sp-backend.test/uploads/orders/${url}`,
                }))
              );

              setIsModalVisible(true);
            }}
          >
            {isAdminOrDriver ? "Upload / View Photos" : "View Photos"}
          </Button>

          {/* Delete only if Admin */}
          {isAdmin && (
            <Button
              type="text"
              style={{ background: "#E4B349 !important" }}
              icon={<DeleteOutlined style={{ color: "red" }} />}
              onClick={() => handleDelete(record.id)}
            />
          )}
        </div>
      ),
    },
    {
      title: "Invoice",
      key: "invoice",
      render: (_, record) => (
        <Button type="default" onClick={() => generateInvoice(record, record)}>
          PDF Invoice
        </Button>
      ),
    },
  ];

  const handleDelete = (id) => {
    confirm({
      title: "Are you sure you want to delete this order?",
      content: `Order ID: ${id}`,
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await payment.softDeleteOrder(id);
          message.success(`Order ${id} deleted successfully`);
          fetchOrders(); // refresh list
        } catch (err) {
          console.error(err);
          message.error("Failed to delete order");
        }
      },
    });
};


  // Search filter
  const filteredData = orders.filter((order) => {
    const query = searchQuery.toLowerCase();
    return (
      order.customer_name?.toLowerCase().includes(query) ||
      order.whatsapp?.toLowerCase().includes(query) ||
      order.address?.toLowerCase().includes(query)
    );
  });

  const menu = {
    items: columns.map((col) => ({
      key: col.key,
      label: (
        <div
          onClick={() => {
            setVisibleColumns((prev) =>
              prev.includes(col.key)
                ? prev.filter((c) => c !== col.key)
                : [...prev, col.key]
            );
          }}
        >
          <input
            type="checkbox"
            checked={visibleColumns.includes(col.key)}
            readOnly
          />{" "}
          {col.title}
        </div>
      ),
    })),
  };

  // Handle Photo Update
  const handleSavePhotos = async () => {
    if (!editingOrder || !isAdminOrDriver) return;

    try {
      const formData = new FormData();

      // 1. Send existing filenames to keep
      const existingFiles = fileList
        .filter((f) => !f.originFileObj) // files without originFileObj are existing
        .map((f) => f.name);

      existingFiles.forEach((filename) => formData.append("existing[]", filename));

      // 2. Send newly added files
      fileList
        .filter((f) => f.originFileObj) // only new files
        .forEach((file) => formData.append("attachment[]", file.originFileObj));

      // 3. Call backend
      await payment.uploadOrderPhotos(editingOrder.id, formData);

      message.success("Photos updated successfully!");
      setIsModalVisible(false);
      setEditingOrder(null);
      setFileList([]);
      fetchOrders();
    } catch (err) {
      console.error(err);
      message.error("Failed to update photos");
    }
  };

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <DefaultLayout>
        {/* Header */}
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Card
              style={{ display: "flex", justifyContent: "space-between" }}
              bodyStyle={{ padding: "12px 24px" }}
            >
              <span>
                <ShoppingCartOutlined /> Orders
              </span>
            </Card>
          </Col>
        </Row>

        {/* Search */}
        <Card style={{ marginTop: 16 }}>
          <Input
            size="large"
            placeholder="Search Orders"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Card>

        {/* Table */}
        <Card style={{ marginTop: 16 }}>
          <Dropdown
            className="mr-10"
            menu={menu}
            open={visible}
            onOpenChange={(isVisible) => setVisible(isVisible)}
            trigger={["click"]}
          >
            <a
              style={{ color: "gray", fontSize: "11px" }}
              onClick={(e) => e.preventDefault()}
            >
              <UnorderedListOutlined /> Columns
            </a>
          </Dropdown>

          <Table
            style={{ marginTop: 12 }}
            columns={columns.filter((col) => visibleColumns.includes(col.key))}
            dataSource={filteredData.map((order) => ({ ...order, key: order.id }))}
            loading={loading}
            rowKey={(record) => record.id}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1200 }}
          />
        </Card>

        {/* Modal for Photo Upload */}
        <Modal
          title={`Photos for Order #${editingOrder?.id || ""}`}
          open={isModalVisible}
          onCancel={() => {
            setIsModalVisible(false);
            setEditingOrder(null);
            setFileList([]);
          }}
          footer={
            isAdminOrDriver ? null : (
              <Button onClick={() => setIsModalVisible(false)}>Close</Button>
            )
          }
          width={600}
        >
          <Upload
            listType="picture-card"
            fileList={fileList}
            multiple
            beforeUpload={() => false} // prevent auto-upload
            onChange={({ fileList: newFileList }) => setFileList(newFileList)}
            disabled={!isAdminOrDriver} // only Admin/Driver can upload
          >
            {isAdminOrDriver && (
              <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Upload</div>
              </div>
            )}
          </Upload>

          {isAdminOrDriver && (
            <Button
              type="primary"
              block
              style={{ marginTop: 16 }}
              onClick={handleSavePhotos}
            >
              Save Photos
            </Button>
          )}
        </Modal>
      </DefaultLayout>
    </Layout>
  );
};

export default OrdersPage;
